// Alpine plus the components that need to ship with it, loaded by a script tag
// in the layout component in place of the CDN build it replaces.
//
// Bundling Alpine rather than pulling it from a CDN pins its version to the
// lockfile, and lets registration and startup happen in one file: components are
// declared before start() walks the DOM, which removes any need for the
// alpine:init handshake a separately delivered bundle would require.
import Alpine from 'alpinejs'
import { DirectUpload } from '@rails/activestorage'

const upload = (file, url, token, onProgress) =>
  new Promise((resolve, reject) => {
    const request = new DirectUpload(file, url, {
      directUploadWillCreateBlobWithXHR (xhr) {
        if (token) xhr.setRequestHeader('X-CSRF-Token', token)
      },

      directUploadWillStoreFileWithXHR (xhr) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100))
        })
      }
    })

    request.create((error, blob) => (error ? reject(error) : resolve(blob.signed_id)))
  })

// Endpoint, token and anything the last attempt already uploaded are published
// on the form by Liquid::FormTag, so the template names the field and nothing
// else. Reading them needs $el, which only exists once Alpine has bound the
// component — hence init() rather than the initial value of `uploads`.
Alpine.data('formUpload', ({ field, name, max = null }) => ({
  name,
  uploads: [],

  // Without rehydrating, a typo in an unrelated field would discard every
  // upload the visitor had already completed.
  init () {
    this.uploads = (this.config.values || []).map(({ signed_id: signedId, upload_id: uploadId, filename }) => ({
      signedId,
      uploadId,
      filename: filename || '',
      progress: 100,
      error: null
    }))
  },

  get config () {
    const uploads = this.$el.closest('form')?.dataset?.uploads

    return (uploads ? JSON.parse(uploads) : {})[field] || {}
  },

  get full () {
    return max !== null && this.uploads.length >= max
  },

  async select (event) {
    const files = Array.from(event.target.files)
    event.target.value = ''

    for (const file of files) {
      if (this.full) break

      const index = this.uploads.push({ signedId: null, filename: file.name, progress: 0, error: null }) - 1
      const entry = this.uploads[index]

      try {
        const { url, token } = this.config

        entry.signedId = await upload(file, url, token, (progress) => { entry.progress = progress })
      } catch (error) {
        entry.error = String(error)
      }
    }
  },

  remove (index) {
    this.uploads.splice(index, 1)
  }
}))

window.Alpine = Alpine
document.addEventListener('DOMContentLoaded', () => Alpine.start())
